// 豆包同声传译 AST 2.0 —— 自包含 protobuf 编解码（无依赖，与 ws-doubao-client.js 同风格）。
//
// ⚠️ 与 dialogue/TTS 的火山 v3 二进制帧完全不同：AST v4 **整条 WebSocket 消息就是一个 protobuf
//    message，没有 v3 那套二进制头**。字段号取自官方 ast_service.proto，2026-07-23 对真上游逐帧
//    验证（过网关 SessionStarted + 源/译文字幕闭环通）。只覆盖 playground 需要的消息子集：
//      TranslateRequest { request_meta=1, event=2, user=3, source_audio=4, target_audio=5, request=6 }
//      TranslateResponse{ response_meta=1, event=2, data=3(bytes), text=4, start_time=5, end_time=6, spk_chg=7 }
//      RequestMeta { Endpoint=1, AppKey=2, ResourceID=4, ConnectionID=5, SessionID=6, Sequence=7 }
//      Audio { format=4, rate=7, bits=8, channel=9, binary_data=14 }
//      ReqParams(ast) { mode=1, source_language=2, target_language=3, speaker_id=4 }
//      ResponseMeta { SessionID=1, Sequence=2, StatusCode=3, Message=4, Billing=5 }
//      Billing { Items=1(repeated), DurationMsec=2, WordCount=3 }  BillingItem { Unit=1, Quantity=2(float) }
//
// 计费口径：网关按墙钟分钟计（adapter durationSource='wallclock'），playground 据连接时长估算；
//    上游 UsageResponse(154) 另带 DurationMsec + 模态 token（仅作展示）。

export const AstEvent = {
  StartSession: 100, FinishSession: 102,
  SessionStarted: 150, SessionFinished: 152, SessionFailed: 153,
  TaskRequest: 200, UsageResponse: 154, AudioMuted: 250,
  SourceSubtitleStart: 650, SourceSubtitleResponse: 651, SourceSubtitleEnd: 652,
  TranslationSubtitleStart: 653, TranslationSubtitleResponse: 654, TranslationSubtitleEnd: 655,
  TTSSentenceStart: 350, TTSResponse: 352, TTSSentenceEnd: 351, TTSEnded: 359,
};

// 火山网关必需的固定 resource（= RequestMeta.Endpoint，缺它上游报 "service name is required"）。
export const AST_RESOURCE_ID = 'volc.service_type.10053';

// 成功状态码（其余非零 = 错误）。
const OK_STATUS = new Set([0, 20000000]);
export const isOkStatus = (s) => s == null || OK_STATUS.has(s);

const enc = new TextEncoder();
const dec = new TextDecoder();

// ───────────────── writer（累积到 number[]，最后转 Uint8Array）─────────────────
function wVarint(a, n) { let v = n; while (v > 0x7f) { a.push((v & 0x7f) | 0x80); v = Math.floor(v / 128); } a.push(v & 0x7f); }
function wTag(a, field, wire) { wVarint(a, field * 8 + wire); }
function wStr(a, field, s) { if (!s) return; const b = enc.encode(s); wTag(a, field, 2); wVarint(a, b.length); for (let i = 0; i < b.length; i++) a.push(b[i]); }
function wInt(a, field, n) { if (!n) return; wTag(a, field, 0); wVarint(a, n); }      // proto3：省略 0
function wIntForce(a, field, n) { wTag(a, field, 0); wVarint(a, n); }                  // 强制写（event）
function wBytesField(a, field, u8) { if (!u8 || !u8.length) return; wTag(a, field, 2); wVarint(a, u8.length); for (let i = 0; i < u8.length; i++) a.push(u8[i]); }
function wMsg(a, field, sub) { if (!sub.length) return; wTag(a, field, 2); wVarint(a, sub.length); for (let i = 0; i < sub.length; i++) a.push(sub[i]); }

function encRequestMeta({ endpoint, resourceId, connectionId, sessionId, sequence }) {
  const a = [];
  wStr(a, 1, endpoint);        // Endpoint
  // AppKey(2) 省略：网关注入 X-Api-* header 鉴权，body AppKey 可空（已验证）。
  wStr(a, 4, resourceId);      // ResourceID
  wStr(a, 5, connectionId);    // ConnectionID
  wStr(a, 6, sessionId);       // SessionID
  wIntForce(a, 7, sequence);   // Sequence（强制写，含 0，与官方 protobufjs 逐字节一致）
  return a;
}
function encAudioConfig({ format, rate, bits, channel }) {
  const a = []; wStr(a, 4, format); wInt(a, 7, rate); wInt(a, 8, bits); wInt(a, 9, channel); return a;
}
function encReqParams({ mode, sourceLanguage, targetLanguage, speakerId }) {
  const a = []; wStr(a, 1, mode); wStr(a, 2, sourceLanguage); wStr(a, 3, targetLanguage); wStr(a, 4, speakerId); return a;
}

// StartSession(100)：s2s 需要 target_audio，s2t 只出字幕不需要。
export function encodeStartSession({ sessionId, connectionId, sequence = 0, mode = 's2s', sourceLanguage, targetLanguage, speakerId, inputRate = 16000, targetFormat = 'pcm', targetRate = 24000 }) {
  const a = [];
  wMsg(a, 1, encRequestMeta({ endpoint: AST_RESOURCE_ID, resourceId: AST_RESOURCE_ID, connectionId, sessionId, sequence }));
  wIntForce(a, 2, AstEvent.StartSession);
  wMsg(a, 3, [...(() => { const u = []; wStr(u, 1, 'xapi-tryit'); wStr(u, 3, 'web'); return u; })()]); // user{uid,platform}
  wMsg(a, 4, encAudioConfig({ format: 'pcm', rate: inputRate, bits: 16, channel: 1 })); // source_audio
  if (mode === 's2s') wMsg(a, 5, encAudioConfig({ format: targetFormat, rate: targetRate })); // target_audio
  wMsg(a, 6, encReqParams({ mode, sourceLanguage, targetLanguage, speakerId }));
  return Uint8Array.from(a);
}

// TaskRequest(200)：一包 Int16@16k PCM 放进 source_audio.binary_data(14)。
export function encodeAudio({ sessionId, connectionId, sequence, pcm }) {
  const a = [];
  wMsg(a, 1, encRequestMeta({ connectionId, sessionId, sequence }));
  wIntForce(a, 2, AstEvent.TaskRequest);
  wMsg(a, 4, (() => { const s = []; wBytesField(s, 14, pcm); return s; })());
  return Uint8Array.from(a);
}

// FinishSession(102)：收尾。
export function encodeFinish({ sessionId, connectionId, sequence }) {
  const a = [];
  wMsg(a, 1, encRequestMeta({ connectionId, sessionId, sequence }));
  wIntForce(a, 2, AstEvent.FinishSession);
  return Uint8Array.from(a);
}

// ───────────────── reader ─────────────────
function readVarint(u8, p) { let shift = 0, result = 0, b; do { b = u8[p.i++]; result += (b & 0x7f) * Math.pow(2, shift); shift += 7; } while (b & 0x80); return result; }
function eachField(u8, start, end, cb) {
  const p = { i: start };
  while (p.i < end) {
    const tag = readVarint(u8, p); const field = Math.floor(tag / 8), wire = tag & 7;
    if (wire === 0) { const v = readVarint(u8, p); cb(field, 0, v, 0, 0); }
    else if (wire === 2) { const len = readVarint(u8, p); const s = p.i; p.i += len; cb(field, 2, 0, s, p.i); }
    else if (wire === 1) { p.i += 8; }                       // 64-bit（本子集用不到）
    else if (wire === 5) { const s = p.i; p.i += 4; cb(field, 5, 0, s, s + 4); } // 32-bit（float）
    else break;
  }
}
const readFloat32 = (u8, s) => new DataView(u8.buffer, u8.byteOffset + s, 4).getFloat32(0, true);

function decodeBillingItem(u8, s, e) {
  const it = { unit: '', quantity: 0 };
  eachField(u8, s, e, (f, w, v, ss, ee) => {
    if (f === 1 && w === 2) it.unit = dec.decode(u8.subarray(ss, ee));
    else if (f === 2 && w === 5) it.quantity = readFloat32(u8, ss);
  });
  return it;
}
function decodeBilling(u8, s, e) {
  const b = { items: [], durationMsec: 0, wordCount: 0 };
  eachField(u8, s, e, (f, w, v, ss, ee) => {
    if (f === 1 && w === 2) b.items.push(decodeBillingItem(u8, ss, ee));
    else if (f === 2 && w === 0) b.durationMsec = v;
    else if (f === 3 && w === 0) b.wordCount = v;
  });
  return b;
}
function decodeMeta(u8, s, e) {
  const m = { sessionId: '', sequence: 0, statusCode: null, message: '', billing: null };
  eachField(u8, s, e, (f, w, v, ss, ee) => {
    if (f === 1 && w === 2) m.sessionId = dec.decode(u8.subarray(ss, ee));
    else if (f === 2 && w === 0) m.sequence = v;
    else if (f === 3 && w === 0) m.statusCode = v;
    else if (f === 4 && w === 2) m.message = dec.decode(u8.subarray(ss, ee));
    else if (f === 5 && w === 2) m.billing = decodeBilling(u8, ss, ee);
  });
  return m;
}

// 解 TranslateResponse → { event, text, audio(Uint8Array|null), startTime, endTime, spkChg, meta }
export function decodeResponse(buf) {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  const out = { event: 0, text: '', audio: null, startTime: 0, endTime: 0, spkChg: false, meta: null };
  eachField(u8, 0, u8.length, (f, w, v, s, e) => {
    if (f === 1 && w === 2) out.meta = decodeMeta(u8, s, e);
    else if (f === 2 && w === 0) out.event = v;
    else if (f === 3 && w === 2) out.audio = u8.slice(s, e);
    else if (f === 4 && w === 2) out.text = dec.decode(u8.subarray(s, e));
    else if (f === 5 && w === 0) out.startTime = v;
    else if (f === 6 && w === 0) out.endTime = v;
    else if (f === 7 && w === 0) out.spkChg = !!v;
  });
  return out;
}

// 从 UsageResponse(154) 的 billing.items 聚合模态 token（展示用）。
export function extractUsage(billing) {
  const u = { inputAudio: 0, outputText: 0, outputAudio: 0, durationMsec: 0 };
  if (!billing) return u;
  u.durationMsec = billing.durationMsec || 0;
  for (const it of billing.items || []) {
    if (it.unit === 'input_audio_tokens') u.inputAudio += it.quantity;
    else if (it.unit === 'output_text_tokens') u.outputText += it.quantity;
    else if (it.unit === 'output_audio_tokens') u.outputAudio += it.quantity;
  }
  return u;
}
